import {
  faBullseye,
  faDownload,
  faList
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex } from 'components';
import { CustomTab } from 'components/Tabs/CustomTab';
import { CustomTabList } from 'components/Tabs/CustomTabList';
import { CustomTabs } from 'components/Tabs/CustomTabs';
import { Button, Checkbox } from 'components/_form';
import { TabPanel } from 'react-tabs';
import { AssessmentBlocks } from '../AssessmentBlocks/AssessmentBlocks';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { useAssessmentContext } from 'contexts/AssessmentContext';
import { AssessmentThemes } from '../AssessmentThemes/AssessmentThemes';
import { AssessmentRadar } from '../AssessmentRadar/AssessmentRadar';
import { Theme } from 'types/methologies';
import { RadarGraphDataset } from 'types/assessments';

const initGraphValueStagesForPlat = [1.025, 2.05, 3.075, 4.1];
const initGraphValueStagesForADMA = [...initGraphValueStagesForPlat, 5.125];

const radarGraphColors = {
  border: [
    'rgb(255, 0, 0)',
    'rgb(153, 0, 255)',
    'rgb(0, 4, 255)',
    'rgb(96, 165, 255)',
    'rgb(96, 255, 255)',
    'rgb(96, 255, 99)',
    'rgba(255, 99, 132, 1)'
  ],
  background: [
    'rgba(255, 0, 0, 0.2)',
    'rgba(153, 0, 255, 0.2)',
    'rgba(0, 4, 255, 0.2)',
    'rgba(96, 165, 255, 0.2)',
    'rgba(96, 255, 255, 0.2)',
    'rgba(96, 255, 99, 0.2)',
    'rgba(255, 99, 132, 0.2)'
  ]
};

export const AssessmentDetails: React.FC = () => {
  const [showLegend, setShowLegend] = useState(false);
  const [axes, setAxes] = useState<Theme[]>([]);
  const [graphTitle, setGraphTitle] = useState('');
  const [datasets, setDatasets] = useState<RadarGraphDataset[]>([]);
  const [radarGraphStages, setRadarGraphStages] = useState<number[]>(
    initGraphValueStagesForPlat
  );

  const { t } = useTranslation();
  const { selectedAssessments } = useAssessmentContext();

  const getRadarGraphColors = useCallback(
    (variant: 'border' | 'background', index: number) => {
      const colorIndex = index % radarGraphColors[variant].length;

      return radarGraphColors[variant][colorIndex];
    },
    []
  );

  // TODO: might be usefull later, we will know after the meeting with client
  // const roundToNumber = useCallback((numbersToRound: number[], num: number) => {
  //   const differenceWithEachNumber: number[] = [];

  //   numbersToRound.map((number) => {
  //     differenceWithEachNumber.push(Math.abs(num - number));
  //   });

  //   const minDifference = Math.min(...differenceWithEachNumber);
  //   const minIndex = differenceWithEachNumber.indexOf(minDifference);

  //   const closestNumber = numbersToRound[minIndex];

  //   return Number(closestNumber);
  // }, []);

  const checkNumberOfSelectedPractices = useCallback(() => {
    const practices: number[] = [];

    if (selectedAssessments.length) {
      selectedAssessments.map((assessment) => {
        if (!practices.includes(assessment.practice?.id)) {
          practices.push(assessment.practice?.id);
        }
      });
    }

    return practices.length;
  }, [selectedAssessments]);

  useEffect(() => {
    if (selectedAssessments.length) {
      const numberOfSelectedPractices = checkNumberOfSelectedPractices();
      if (numberOfSelectedPractices === 1) {
        setAxes(selectedAssessments[0].practice?.themes);
        setGraphTitle(selectedAssessments[0].practice?.name);
      } else if (numberOfSelectedPractices > 1) {
        setRadarGraphStages(initGraphValueStagesForADMA);
      }
    }
  }, [checkNumberOfSelectedPractices, selectedAssessments]);

  const makeDatasets = useCallback(() => {
    const datasets: RadarGraphDataset[] = [];

    if (selectedAssessments.length) {
      selectedAssessments.map((assessment, i) => {
        const dataset: RadarGraphDataset = {
          label: assessment.practice?.name + ' | ' + assessment.name,
          data: [],
          backgroundColor: getRadarGraphColors('background', i),
          borderColor: getRadarGraphColors('border', i),
          borderWidth: 1
        };

        assessment.practice?.themes.map((theme) => {
          dataset.data.push(theme.score);
        });

        datasets.push(dataset);
      });
    }

    return datasets;
  }, [getRadarGraphColors, selectedAssessments]);

  useEffect(() => {
    setDatasets(makeDatasets());
  }, [makeDatasets]);

  return (
    <CustomTabs selectedTabClassName="is-selected">
      <CustomTabList style={{ borderBottom: 'none' }}>
        <Flex flexWrap="wrap">
          <CustomTab>{t('assessmentsView.blocks')}</CustomTab>
          <CustomTab>{t('assessmentsView.themes')}</CustomTab>
          <CustomTab>{t('assessmentsView.radar')}</CustomTab>
          <CustomTab>{t('assessmentsView.data')}</CustomTab>

          <Flex ml="auto" my="auto" gap="20px">
            <Flex alignItems="center">
              <Checkbox
                checked={showLegend}
                onChange={(val) => {
                  setShowLegend(val.target.checked);
                }}
              />
              <FontAwesomeIcon icon={faList} />
            </Flex>

            <Flex alignItems="center">
              <Checkbox />
              <FontAwesomeIcon icon={faBullseye} />
            </Flex>

            <Button variant="secondary" bordered onClick={() => {}}>
              <FontAwesomeIcon icon={faDownload} /> {t('buttons.save')}
            </Button>
          </Flex>
        </Flex>
      </CustomTabList>

      <TabPanel>
        {selectedAssessments.length ? (
          <AssessmentBlocks showLegend={showLegend} />
        ) : (
          ''
        )}
      </TabPanel>

      <TabPanel>
        <AssessmentThemes />
      </TabPanel>

      <TabPanel>
        <AssessmentRadar
          axes={axes}
          datasets={datasets}
          graphStages={radarGraphStages}
        />
      </TabPanel>

      <TabPanel>-</TabPanel>
    </CustomTabs>
  );
};
